import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useConfig } from '@/config/useConfig';

interface FeatureFlagContextValue {
  flags: Record<string, boolean>;
  isLoading: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  isLoading: true,
});

function parseFlagsFromCsv(csv: string): Record<string, boolean> {
  const flags: Record<string, boolean> = {};
  csv
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean)
    .forEach((flag) => {
      flags[flag] = true;
    });
  return flags;
}

function parseFlagsFromQueryString(): Record<string, boolean> {
  const params = new URLSearchParams(window.location.search);
  const override = params.get('featureFlags');
  if (!override) return {};
  return parseFlagsFromCsv(override);
}

interface FeatureFlagProviderProps {
  children: ReactNode;
  overrides?: Record<string, boolean>;
}

export function FeatureFlagProvider({
  children,
  overrides,
}: FeatureFlagProviderProps): React.ReactElement {
  const config = useConfig();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    async function loadFlags(): Promise<void> {
      // Priority 1: Query string overrides (disabled in production for security)
      const queryFlags = config.environment === 'production' ? {} : parseFlagsFromQueryString();

      // Priority 2: Fetch from backend endpoint (skip if no endpoint configured or in local dev
      // where the endpoint doesn't exist yet — AD-007 notes the API-side config endpoint is deferred)
      let endpointFlags: Record<string, boolean> = {};
      if (config.featureFlagsEndpoint && config.environment !== 'local') {
        try {
          const response = await fetch(config.featureFlagsEndpoint);
          if (response.ok) {
            const contentType = response.headers.get('content-type') ?? '';
            if (!contentType.includes('application/json')) {
              // Non-JSON response (e.g., SWA fallback to index.html) — skip
            } else {
              const data = (await response.json()) as unknown;
              const rawFeatureFlags =
                typeof data === 'object' && data !== null && 'featureFlags' in data
                  ? (data as { featureFlags?: unknown }).featureFlags
                  : undefined;

              if (typeof rawFeatureFlags === 'object' && rawFeatureFlags !== null) {
                endpointFlags = Object.entries(rawFeatureFlags as Record<string, unknown>).reduce(
                  (acc, [key, value]) => {
                    if (typeof value === 'boolean') acc[key] = value;
                    return acc;
                  },
                  {} as Record<string, boolean>,
                );
              }
            }
          }
        } catch {
          // Fall through to env var fallback
        }
      }

      // Priority 3: Static config.json featureFlags (per-environment, set at deployment)
      const rawConfigFlags = config.featureFlags ?? {};
      const configFlags: Record<string, boolean> = Object.entries(rawConfigFlags).reduce(
        (acc, [key, value]) => {
          if (typeof value === 'boolean') {
            acc[key] = value;
          } else {
            // eslint-disable-next-line no-console
            console.warn(
              `[FeatureFlags] config.json featureFlags["${key}"] has non-boolean value (${typeof value}): "${value}". Entry ignored.`,
            );
          }
          return acc;
        },
        {} as Record<string, boolean>,
      );

      // Priority 4: VITE env var fallback (lowest)
      const envCsv = import.meta.env.VITE_APP_ACTIVE_FEATURE_FLAGS as string | undefined;
      const envFlags = envCsv ? parseFlagsFromCsv(envCsv) : {};

      // Priority 5: Local dev defaults — enable demo flags so the app is usable during development
      const localDefaults: Record<string, boolean> =
        config.environment === 'local' ? { aita_735698: true, aita_ping: true } : {};

      if (!cancelled) {
        setFlags({ ...localDefaults, ...envFlags, ...configFlags, ...endpointFlags, ...overrides, ...queryFlags });
        setIsLoading(false);
      }
    }

    void loadFlags();
    return () => {
      cancelled = true;
    };
  }, [config.featureFlagsEndpoint, config.featureFlags, config.environment, overrides]);

  return (
    <FeatureFlagContext.Provider value={{ flags, isLoading }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlagContext(): FeatureFlagContextValue {
  return useContext(FeatureFlagContext);
}

export { parseFlagsFromCsv as _parseFlagsFromCsv };
